import { getDestinationQuery } from "./destination";
import { formatTrackingLocation } from "./trackingStatus";
import type { PublicTrackingStatus } from "./types";

export interface ScanStop {
  /** Geocoding query — a 5-digit ZIP when present, else "City, ST". */
  query: string;
  /** Human label for the stop. */
  label: string;
  /** `last` = the most recent scan (current position); `destination` = the delivery ZIP. */
  kind: "scan" | "last" | "destination";
}

/**
 * Ordered map stops for a PUBLIC tracking response: one per scan event
 * (geocoded by "City, ST"; ZIP3 is too coarse to plot well and is filtered
 * by geocode relevance), collapsing consecutive duplicates, tagging the
 * latest as `last`, then appending the destination ZIP.
 */
export function trackingEventStops(data: PublicTrackingStatus): ScanStop[] {
  const stops: ScanStop[] = [];
  for (const event of data.events) {
    const { city, state, zip3 } = event.location;
    const query = [city, state].filter(Boolean).join(", ") || zip3 || "";
    if (!query) continue;
    stops.push({
      query,
      label: formatTrackingLocation(event.location),
      kind: "scan",
    });
  }

  const out: ScanStop[] = [];
  for (const stop of stops) {
    const prev = out[out.length - 1];
    if (prev && prev.query === stop.query) continue;
    out.push(stop);
  }

  const lastScanIndex = out.map((s) => s.kind).lastIndexOf("scan");
  if (lastScanIndex >= 0) {
    out[lastScanIndex] = { ...out[lastScanIndex], kind: "last" };
  }

  const destinationQuery = getDestinationQuery(data);
  if (destinationQuery) {
    out.push({
      query: destinationQuery,
      label: "Destination",
      kind: "destination",
    });
  }

  return out;
}
